// Общи части на формите-модали в Конфигурация (монитори, устройства):
// шел (хедър + футър), стъпка с етикет и scoped CSS за antd класовете.

import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  // Плътни акцентни фонове — еднакви в двете теми.
  brand: "#D97706",
  brandDeep: "#B45309",
  brandTint: "var(--color-brand-tint)",
};

// Шелът на форма-модал: хедър (икона + kicker + заглавие + затваряне),
// тяло (children — извикващият си носи решетката) и футър Отказ/Запази.
// Височина до ръба на екрана; тялото скролва само като предпазен fallback.
export const FormModalShell = ({
  icon,
  kicker,
  title,
  titleExtra,
  isNarrow,
  saveLabel,
  canSave,
  isSubmitting,
  onSave,
  onCancel,
  children,
}: {
  icon: React.ReactNode;
  kicker: string;
  title: string;
  // Незадължителен елемент до заглавието (напр. баджът „Шпикер").
  titleExtra?: React.ReactNode;
  isNarrow: boolean;
  saveLabel: string;
  canSave: boolean;
  isSubmitting: boolean;
  onSave: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: R.surface,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 48px)",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: isNarrow ? "14px 16px" : "16px 22px",
          borderBottom: `1px solid ${R.border}`,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
          background: R.surface,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: R.brandTint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-brand-deep-ink)",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: R.textMuted,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              lineHeight: 1.2,
            }}
          >
            {kicker}
          </div>
          <div
            className="flex items-center"
            style={{ gap: 10, marginTop: 3, minWidth: 0 }}
          >
            <span
              style={{
                fontSize: isNarrow ? 16 : 18,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </span>
            {titleExtra}
          </div>
        </div>
        <button
          onClick={onCancel}
          aria-label="Close"
          style={{
            height: 30,
            width: 30,
            borderRadius: 6,
            background: "transparent",
            border: "none",
            color: R.textMuted,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 120ms, color 120ms",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-shade-1)";
            e.currentTarget.style.color = R.ink;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = R.textMuted;
          }}
        >
          <CloseOutlined style={{ fontSize: 14 }} />
        </button>
      </div>

      {children}

      {/* Footer */}
      <div
        style={{
          borderTop: `1px solid ${R.border}`,
          padding: isNarrow ? "12px 16px" : "12px 22px",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "flex-end",
          gap: 10,
          flexShrink: 0,
          background: R.surface,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          style={{
            height: 42,
            padding: "0 18px",
            borderRadius: 10,
            border: `1px solid ${R.border}`,
            background: "transparent",
            color: R.textSoft,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: isSubmitting ? "not-allowed" : "pointer",
            transition: "background 120ms",
            flex: isNarrow ? 1 : undefined,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting)
              e.currentTarget.style.background = "var(--color-shade-1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          style={{
            height: 42,
            padding: "0 22px",
            minWidth: isNarrow ? undefined : 200,
            borderRadius: 10,
            border: "none",
            background: canSave ? R.brand : "rgba(217,119,6,0.35)",
            color: "#fff",
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: -0.2,
            cursor: canSave ? "pointer" : "not-allowed",
            transition: "background 120ms",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flex: isNarrow ? 1 : undefined,
          }}
          onMouseEnter={(e) => {
            if (canSave) e.currentTarget.style.background = R.brandDeep;
          }}
          onMouseLeave={(e) => {
            if (canSave) e.currentTarget.style.background = R.brand;
          }}
        >
          {isSubmitting && (
            <span
              style={{
                width: 14,
                height: 14,
                border: "2px solid rgba(255,255,255,0.5)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "rspin 900ms linear infinite",
              }}
            />
          )}
          {saveLabel}
        </button>
      </div>
    </div>
  );
};

// Стъпка от форма: БЯЛА секция-карта върху бежовото тяло на модала, с
// етикет (+ незадължителна дясна пилюла) и съдържание; fullRow я разпъва
// на целия ред, когато е в решетка.
export const FormStepField = ({
  label,
  right,
  fullRow = false,
  children,
}: {
  label: string;
  right?: React.ReactNode;
  fullRow?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className="flex min-w-0 flex-col gap-[8px] rounded-[12px] border border-shade-2 bg-surface px-[14px] py-[12px]"
    style={{
      // Позицията в решетката зависи от fullRow — runtime стойност.
      gridColumn: fullRow ? "1 / -1" : undefined,
    }}
  >
    <div className="flex min-h-[18px] items-center justify-between gap-[8px]">
      <span className="text-[10.5px] font-bold uppercase tracking-[1px] text-muted">
        {label}
      </span>
      {right}
    </div>
    {children}
  </div>
);

// Scoped CSS за antd Input/Select/Checkbox във формите (недостижими за
// Tailwind) — живее под класа .cfg-form, общ за всички форми-модали.
export const ConfigFormAntdCss = () => (
  <style>{`
    @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
    .cfg-form .ant-input {
      border-radius: 10px !important;
      border-color: var(--color-shade-3) !important;
      min-height: 42px !important;
      height: 42px !important;
      padding: 4px 12px !important;
      background: var(--color-surface) !important;
      font-size: 13px;
      color: var(--color-ink);
      transition: border-color 120ms, box-shadow 120ms;
    }
    .cfg-form .ant-input:hover,
    .cfg-form .ant-input:focus {
      border-color: #D97706 !important;
      box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
    }
    .cfg-form .ant-input::placeholder { color: var(--color-muted) !important; }
    .cfg-form .ant-select-selector {
      border-radius: 10px !important;
      border-color: var(--color-shade-3) !important;
      min-height: 42px !important;
      padding: 4px 11px !important;
      background: var(--color-surface) !important;
      transition: border-color 120ms, box-shadow 120ms;
    }
    .cfg-form .ant-select-focused .ant-select-selector,
    .cfg-form .ant-select-selector:hover {
      border-color: #D97706 !important;
      box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
    }
    .cfg-form .ant-select-selection-placeholder { color: var(--color-muted) !important; font-size: 13px; }
    .cfg-form .ant-select-selection-item { font-size: 13px; color: var(--color-ink); }
    .cfg-form .ant-select-single { height: 42px !important; }
    .cfg-form .ant-select-single .ant-select-selector { height: 42px !important; align-items: center; }
    .cfg-form .ant-select-multiple .ant-select-selection-item {
      background: var(--color-brand-tint); border-color: var(--color-brand-tint2); color: var(--color-brand-deep-ink); border-radius: 6px;
    }
    .cfg-form .ant-checkbox-inner { border-color: var(--color-shade-3) !important; border-radius: 5px; }
    .cfg-form .ant-checkbox:hover .ant-checkbox-inner { border-color: #D97706 !important; }
    .cfg-form .ant-checkbox-checked .ant-checkbox-inner { background-color: #D97706 !important; border-color: #D97706 !important; }
    .cfg-form .ant-checkbox-checked::after { border-color: #D97706 !important; }
    .cfg-form .ant-checkbox-wrapper { color: var(--color-ink); font-size: 13.5px; align-items: center; }
    /* По-едри чекбокси в „Опции" — мащабираме целия бокс (тикчето с него). */
    .cfg-form .ant-checkbox-wrapper .ant-checkbox { transform: scale(1.3); transform-origin: center; margin-right: 4px; }
  `}</style>
);
